/* ==========================================================================
   Configurateur — L'Atelier Mouradi
   Le front ne connait AUCUN nom de fichier : tout vient de manifest.json.
   ========================================================================== */

const $ = (s, r = document) => r.querySelector(s);

const etat = {
  manifest: null,
  modele: null,
  choix: {},          // { groupe: {piece, variante} }
  familleActive: {},  // { groupe: piece }  — onglet choisi quand un groupe
                      //   regroupe plusieurs familles (cadran, bracelet)
};

/* ------------------------------------------------------------------ outils */

const euros = (n) =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR',
    maximumFractionDigits: 0 }).format(n);

/**
 * Le site est servi en WebP, pas en PNG.
 *
 * Les PNG d'origine restent la reference de travail — c'est sur eux que
 * portent le controle d'alignement et la regeneration — mais ils pesent cinq
 * fois plus lourd pour un rendu identique a l'ecran. Le site n'embarque donc
 * que les WebP, et ne reference jamais un PNG.
 */
const RACINE = document.getElementById('configurateur');
const BASE_IMAGES = RACINE.dataset.base;
const BASE_HD = RACINE.dataset.baseHd || BASE_IMAGES;   // 1400 px : dans Contenu > Fichiers
const chemin = (m, fichier, largeur) =>
  `${largeur >= 800 ? BASE_HD : BASE_IMAGES}cfg-${m.id}-${largeur}-` +
  fichier.split('/').pop().replace(/\.png$/, '.webp');

/** Adresse par defaut : la plus grande largeur disponible. */
const url = (m, v) =>
  chemin(m, v.fichier, m.largeursWeb[m.largeursWeb.length - 1]);

/** Vignette : la plus petite largeur suffit. */
const vignetteUrl = (m, v) => chemin(m, v.fichier, m.largeursWeb[0]);

/** srcset complet, aux largeurs declarees dans le manifest. */
function srcset(m, fichier) {
  return m.largeursWeb.map((l) => `${chemin(m, fichier, l)} ${l}w`).join(', ');
}

/* ------------------------------------------------------- cache des visuels */

/**
 * Rien ne s'affiche tant que l'image n'est pas prete.
 *
 * Changer le `src` d'une image visible la vide instantanement : le calque
 * disparait, le fond de page apparait a travers, et on voit un eclair blanc
 * avant que la nouvelle image arrive. On decode donc toujours hors ecran,
 * et on ne touche au calque affiche qu'une fois l'image reellement prete.
 */
const cache = new Map();

function precharger(src, srcs) {
  if (cache.has(src)) return cache.get(src);
  const img = new Image();
  img.decoding = 'async';
  if (srcs) { img.srcset = srcs; img.sizes = '560px'; }
  img.src = src;
  const pret = (img.decode ? img.decode() : Promise.resolve())
    .catch(() => new Promise((ok) => { img.onload = ok; img.onerror = ok; }))
    .then(() => img);
  cache.set(src, pret);
  return pret;
}

/** Precharge en tache de fond toutes les variantes du modele courant. */
function prechargerModele(m) {
  const files = [];
  for (const p of Object.values(m.pieces)) {
    for (const v of p.variantes) files.push(v);
  }
  let i = 0;
  const suivant = () => {
    if (i >= files.length || etat.modele !== m) return;
    const v = files[i++];
    precharger(url(m, v), srcset(m, v.fichier)).then(() => {
      // Une image a la fois : le prechargement ne doit pas disputer la bande
      // passante au calque que l'utilisateur attend maintenant.
      ('requestIdleCallback' in window
        ? requestIdleCallback(suivant, { timeout: 400 })
        : setTimeout(suivant, 60));
    });
  };
  suivant();
}

/* -------------------------------------------------------------- rendu scene */

function calquesOrdonnes() {
  const m = etat.modele;
  return (m.calques || [])
    .slice()
    .sort((a, b) => a.ordre - b.ordre)
    .map((c) => {
      const choix = etat.choix[c.groupe];
      if (!choix) return null;
      const piece = m.pieces[choix.piece];
      const v = piece?.variantes.find((x) => x.id === choix.variante);
      return v ? { groupe: c.groupe, variante: v } : null;
    })
    .filter(Boolean);
}

let jetonRendu = 0;

async function rendreScene() {
  const scene = $('#montre');
  const m = etat.modele;
  const calques = calquesOrdonnes();
  const jeton = ++jetonRendu;

  // On charge tout avant de toucher a l'affichage : sinon un calque change
  // pendant que le voisin charge encore, et la montre se montre a moitie
  // habillee le temps d'un battement.
  const images = await Promise.all(
    calques.map((c) => precharger(url(m, c.variante), srcset(m, c.variante.fichier))));
  if (jeton !== jetonRendu) return;   // un autre choix est arrive entre-temps

  calques.forEach((c, i) => {
    let el = scene.children[i];
    if (!el) {
      el = document.createElement('img');
      el.alt = '';
      el.decoding = 'async';
      scene.appendChild(el);
    }
    const src = images[i].currentSrc || images[i].src;
    if (el.src !== src) {
      el.srcset = images[i].srcset;
      el.sizes = images[i].sizes;
      el.src = images[i].src;
    }
    el.style.zIndex = String(i + 1);
    // Le groupe sert au style : les aiguilles portent une ombre, pas le reste.
    el.dataset.calque = c.groupe;
    el.classList.add('visible');
  });
  while (scene.children.length > calques.length) scene.lastChild.remove();
  scene.classList.add('prete');
}

/* ------------------------------------------------------------ regles metier */

/** Applique les contraintes du modele pour deduire le mouvement impose. */
function mouvementDeduit() {
  const m = etat.modele;
  for (const c of m.contraintes || []) {
    const cible = c.si || {};
    const trouve = Object.values(etat.choix).some((ch) =>
      (!cible.piece || cible.piece === ch.piece) &&
      (!cible.variante || cible.variante === ch.variante));
    if (trouve && c.alors?.mouvement) {
      return { code: c.alors.mouvement, ...m.mouvements[c.alors.mouvement],
               message: c.message };
    }
  }
  const seul = Object.keys(m.mouvements || {});
  return seul.length === 1
    ? { code: seul[0], ...m.mouvements[seul[0]], message: '' }
    : null;
}

/** Prix unique, quelle que soit la personnalisation (public/config.js). */
const PRIX_FIXE = Number(RACINE.dataset.prix) || 230;
const VARIANTE = RACINE.dataset.variante;
const LIEN_CGV = RACINE.dataset.cgv || '';

/* --------------------------------------------------------------- composants */

function pastille(v, actif, onClick) {
  const b = document.createElement('button');
  b.className = 'pastille';
  b.type = 'button';
  b.title = v.libelle;
  b.setAttribute('aria-label', v.libelle);
  b.setAttribute('aria-pressed', String(actif));
  b.style.background = v.couleur || '#999';
  b.onclick = onClick;
  return b;
}

function vignette(v, actif, apercu, onClick) {
  const b = document.createElement('button');
  b.className = 'vignette';
  b.type = 'button';
  b.setAttribute('aria-pressed', String(actif));
  b.onclick = onClick;
  const a = document.createElement('span');
  a.className = 'apercu';
  a.style.backgroundImage = `url("${apercu}")`;
  b.appendChild(a);
  const t = document.createElement('span');
  t.className = 'vignette-nom';
  t.textContent = v.libelle;
  b.appendChild(t);
  return b;
}

function onglets(noms, actif, onPick) {
  const d = document.createElement('div');
  d.className = 'onglets';
  d.setAttribute('role', 'tablist');
  noms.forEach(({ id, texte }) => {
    const b = document.createElement('button');
    b.className = 'onglet';
    b.type = 'button';
    b.setAttribute('role', 'tab');
    b.setAttribute('aria-selected', String(id === actif));
    b.textContent = texte;
    b.onclick = () => onPick(id);
    d.appendChild(b);
  });
  return d;
}

function groupe(titre, valeur, contenu) {
  const s = document.createElement('section');
  s.className = 'groupe';
  const h = document.createElement('div');
  h.className = 'groupe-entete';
  h.innerHTML = `<span class="groupe-titre">${titre}</span>
                 <span class="groupe-valeur"></span>`;
  h.querySelector('.groupe-valeur').textContent = valeur;
  s.appendChild(h);
  contenu.forEach((c) => c && s.appendChild(c));
  return s;
}

/* ------------------------------------------------------------------ panneau */

function rendrePanneau() {
  const m = etat.modele;
  const volet = $('#controles');
  volet.textContent = '';

  const libelleDe = (g) => {
    const c = etat.choix[g];
    if (!c) return '—';
    return m.pieces[c.piece]?.variantes.find((x) => x.id === c.variante)?.libelle ?? '—';
  };

  // L'ordre d'AFFICHAGE des reglages n'est pas celui des calques : on suit
  // les etapes de montage telles que l'atelier les presente — le boitier
  // d'abord, le bracelet en dernier — alors que l'empilement des images va
  // du bracelet (au fond) a la trotteuse (devant).
  const ORDRE_REGLAGES = ['boitier', 'cadran', 'mouvement', 'aiguilles',
                          'trotteuse', 'bracelet'];
  const rang = (g) => {
    const i = ORDRE_REGLAGES.indexOf(g);
    return i === -1 ? ORDRE_REGLAGES.length : i;
  };
  const calques = (m.calques || []).slice()
    .sort((a, b) => rang(a.groupe) - rang(b.groupe));

  for (const calque of calques) {
    if (calque.fige) continue;                       // couche non choisie
    const familles = calque.pieces.filter((p) => m.pieces[p]);
    if (!familles.length) continue;

    const elems = [];
    const famille = etat.familleActive[calque.groupe] || familles[0];

    // Plusieurs familles sous un meme calque (cadran ventilo/lisse,
    // bracelet metal/silicone/cuir) : un jeu d'onglets les reunit.
    if (familles.length > 1) {
      elems.push(onglets(
        familles.map((f) => ({ id: f, texte: m.pieces[f].libelle
          .replace(/^(Cadran|Bracelet)\s*/i, '') || m.pieces[f].libelle })),
        famille,
        (f) => {
          etat.familleActive[calque.groupe] = f;
          etat.choix[calque.groupe] = { piece: f, variante: m.pieces[f].variantes[0].id };
          tout();
        }));
    }

    const vs = m.pieces[famille].variantes;
    const choixCourant = etat.choix[calque.groupe];

    if (calque.groupe === 'boitier') {
      const grille = document.createElement('div');
      grille.className = 'vignettes';
      vs.forEach((v) => grille.appendChild(vignette(
        v, choixCourant?.variante === v.id, vignetteUrl(m, v),
        () => { etat.choix[calque.groupe] = { piece: famille, variante: v.id }; tout(); })));
      elems.push(grille);

    } else if (calque.groupe === 'aiguilles' && vs.some((v) => v.forme)) {
      // Deux axes : la forme d'abord, la finition ensuite.
      const courant = vs.find((v) => v.id === choixCourant?.variante) ?? vs[0];
      const formes = [...new Set(vs.map((v) => v.forme).filter(Boolean))];
      if (formes.length > 1) {
        elems.push(onglets(
          formes.map((f) => ({ id: f, texte: f.charAt(0).toUpperCase() + f.slice(1) })),
          courant.forme,
          (f) => {
            const cible = vs.find((v) => v.forme === f && v.finition === courant.finition)
                       ?? vs.find((v) => v.forme === f);
            etat.choix[calque.groupe] = { piece: famille, variante: cible.id };
            tout();
          }));
      }
      const past = document.createElement('div');
      past.className = 'pastilles';
      vs.filter((v) => v.forme === courant.forme).forEach((v) => past.appendChild(
        pastille(v, choixCourant?.variante === v.id,
          () => { etat.choix[calque.groupe] = { piece: famille, variante: v.id }; tout(); })));
      elems.push(past);

    } else {
      const past = document.createElement('div');
      past.className = 'pastilles';
      vs.forEach((v) => past.appendChild(
        pastille(v, choixCourant?.variante === v.id && choixCourant?.piece === famille,
          () => { etat.choix[calque.groupe] = { piece: famille, variante: v.id }; tout(); })));
      elems.push(past);
    }

    // Le mouvement se lit sur le cadran : on l'annonce juste sous lui.
    if (calque.groupe === 'cadran') {
      const mv = mouvementDeduit();
      if (mv) {
        const box = document.createElement('div');
        box.className = 'mouvement';
        box.innerHTML = '<span class="puce"></span><div>' +
          `<div class="mouvement-nom"></div><div class="mouvement-detail"></div></div>`;
        box.querySelector('.mouvement-nom').textContent = mv.libelle;
        box.querySelector('.mouvement-detail').textContent = mv.message || mv.detail || '';
        elems.push(box);
      }
    }

    volet.appendChild(groupe(calque.libelle, libelleDe(calque.groupe), elems));
  }

  // --- Barre de bas de volet -----------------------------------------------
  // Un seul prix, affiche tel quel : aucune option ne le change.
  const mv = mouvementDeduit();
  const barre = document.createElement('div');
  barre.className = 'barre-prix';
  barre.innerHTML = `
    <div class="prix-bloc">
      <div class="prix-label">Votre montre</div>
      <div class="prix-montant"></div>
      <div class="prix-detail"></div>
    </div>
    <button class="bouton-ajout" type="button">Commander</button>`;
  barre.querySelector('.prix-montant').textContent = `${euros(PRIX_FIXE)} TTC`;
  barre.querySelector('.prix-detail').textContent =
    `${mv ? mv.libelle : 'Mouvement automatique'} · livraison offerte`;
  barre.querySelector('button').onclick = () => recapitulatif(mv);
  volet.appendChild(barre);
}

/* --------------------------------------------------------------- commande */

/**
 * Toutes les options choisies, dans l'ordre de l'atelier, sous la forme
 * [{ cle, valeur }] : c'est exactement ce qui part dans Shopify comme
 * attributs de la ligne de panier, et ce que l'atelier lira sur la commande.
 */
function optionsChoisies(mv) {
  const m = etat.modele;
  const options = [{ cle: 'Modèle', valeur: m.nom }];
  const ORDRE = ['boitier', 'cadran', 'aiguilles', 'trotteuse', 'bracelet'];
  const calques = (m.calques || []).filter((c) => !c.fige && etat.choix[c.groupe])
    .sort((a, b) => ORDRE.indexOf(a.groupe) - ORDRE.indexOf(b.groupe));
  for (const c of calques) {
    const ch = etat.choix[c.groupe];
    const piece = m.pieces[ch.piece];
    const v = piece?.variantes.find((x) => x.id === ch.variante);
    let valeur = v?.libelle ?? ch.variante;
    // Plusieurs familles dans un meme calque (bracelet metal / cuir...) :
    // la famille fait partie du choix.
    if (c.pieces.filter((f) => m.pieces[f]).length > 1) {
      const famille = piece.libelle.replace(/^(Cadran|Bracelet)\s*/i, '');
      if (famille) valeur = `${famille.charAt(0).toUpperCase()}${famille.slice(1)} · ${valeur}`;
    }
    options.push({ cle: c.libelle, valeur });
  }
  if (mv) options.push({ cle: 'Mouvement', valeur: mv.libelle });
  return options;
}

/** Reference technique, pour l'atelier : invisible pour le client
    (Shopify masque au checkout les attributs commencant par « _ »). */
function referenceTechnique() {
  const m = etat.modele;
  return [m.id].concat((m.calques || []).filter((c) => !c.fige && etat.choix[c.groupe])
    .map((c) => `${etat.choix[c.groupe].piece}--${etat.choix[c.groupe].variante}`)).join(' | ');
}

class ErreurCommande extends Error {}

/** Panier Shopify natif : on vide, on ajoute la montre avec ses options, puis paiement. */
async function creerPanierShopify(options) {
  if (!VARIANTE) {
    throw new ErreurCommande("Le paiement en ligne n'est pas encore activé. Contactez-nous pour commander cette configuration.");
  }
  const properties = {};
  options.forEach((o) => { properties[o.cle] = String(o.valeur); });
  properties['_Référence'] = referenceTechnique();
  const controle = new AbortController();
  const minuterie = setTimeout(() => controle.abort(), 15000);
  try {
    await fetch('/cart/clear.js', { method: 'POST', signal: controle.signal });
    const rep = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(VARIANTE), quantity: 1, properties }] }),
      signal: controle.signal,
    });
    if (!rep.ok) {
      let corps = null; try { corps = await rep.json(); } catch { /* */ }
      console.error('Shopify panier', rep.status, corps);
      throw new ErreurCommande(corps?.description
        ? `Commande refusée par la boutique : ${corps.description}`
        : "La commande n'a pas pu être préparée. Réessayez ou contactez-nous.");
    }
  } catch (e) {
    if (e instanceof ErreurCommande) throw e;
    throw new ErreurCommande(e.name === 'AbortError'
      ? 'La boutique ne répond pas. Vérifiez votre connexion et réessayez.'
      : 'Connexion à la boutique impossible. Vérifiez votre connexion et réessayez.');
  } finally {
    clearTimeout(minuterie);
  }
  return '/checkout';
}

function recapitulatif(mv) {
  const m = etat.modele;
  const options = optionsChoisies(mv);

  const fond = document.createElement('div');
  fond.className = 'modale-fond';
  fond.innerHTML = `
    <div class="modale" role="dialog" aria-modal="true" aria-label="Récapitulatif de la commande">
      <h2 class="modale-titre"></h2>
      <dl class="recap"></dl>
      <p class="modale-prix"></p>
      <p class="modale-note">Paiement sécurisé par Shopify. Livraison offerte,
        sous 2 à 3 semaines. Garantie 2 ans.</p>
      <p class="modale-cgv"></p>
      <p class="modale-erreur" role="alert" hidden></p>
      <button class="bouton-ajout" id="commander" type="button">Commander</button>
      <a class="bouton-ajout bouton-secours" id="commande-whatsapp" target="_blank" rel="noopener" hidden>
        Commander par WhatsApp</a>
      <button class="bouton-ajout bouton-fermer" type="button">Fermer</button>
    </div>`;
  fond.querySelector('.modale-titre').textContent = m.nom;
  const dl = fond.querySelector('.recap');
  for (const o of options.slice(1)) {          // le modele est deja le titre
    const dt = document.createElement('dt'); dt.textContent = o.cle;
    const dd = document.createElement('dd'); dd.textContent = o.valeur;
    dl.append(dt, dd);
  }
  fond.querySelector('.modale-prix').textContent = `Total : ${euros(PRIX_FIXE)} TTC`;
  fond.querySelector('.modale-cgv').innerHTML = 'En commandant, vous acceptez nos '
    + (LIEN_CGV ? `<a href="${LIEN_CGV}" target="_blank" rel="noopener">conditions générales de vente</a>` : 'conditions générales de vente')
    + '. Montre fabriquée selon vos choix : pas de droit de rétractation (art. L221-28 C. conso.), garantie légale de 2 ans.';

  // Solution de secours si le paiement en ligne echoue : la configuration
  // part en clair sur WhatsApp, rien a ressaisir.
  const texte = [`Bonjour, je souhaite commander une ${m.nom} (${euros(PRIX_FIXE)}) :`]
    .concat(options.slice(1).map((o) => `- ${o.cle} : ${o.valeur}`)).join('\n');
  const secours = fond.querySelector('#commande-whatsapp');
  secours.href = 'https://wa.me/33616768285?text=' + encodeURIComponent(texte);

  const bouton = fond.querySelector('#commander');
  const erreur = fond.querySelector('.modale-erreur');
  let enCours = false;
  bouton.onclick = async () => {
    if (enCours) return;
    enCours = true;
    bouton.disabled = true;
    bouton.textContent = 'Préparation du paiement…';
    erreur.hidden = true;
    try {
      const url = await creerPanierShopify(options);
      window.location.assign(url);           // le bouton reste desactive
    } catch (e) {
      erreur.textContent = e instanceof ErreurCommande ? e.message
        : 'Une erreur inattendue est survenue. Réessayez ou contactez-nous.';
      if (!(e instanceof ErreurCommande)) console.error(e);
      erreur.hidden = false;
      secours.hidden = false;
      bouton.disabled = false;
      bouton.textContent = 'Réessayer';
      enCours = false;
    }
  };

  const fermer = () => { if (!enCours) fond.remove(); };
  fond.querySelector('.bouton-fermer').onclick = fermer;
  fond.onclick = (e) => { if (e.target === fond) fermer(); };
  document.addEventListener('keydown', function esc(e) {
    if (e.key === 'Escape') { fermer(); document.removeEventListener('keydown', esc); }
  });
  document.body.appendChild(fond);
  bouton.focus();
}

// Retour depuis le paiement (bouton precedent du navigateur) : la page peut
// revenir du cache avec le bouton encore desactive. On repart propre.
window.addEventListener('pageshow', (e) => {
  if (e.persisted) document.querySelectorAll('.modale-fond').forEach((f) => f.remove());
});

/* ----------------------------------------------------------------- pilotage */

function tout() { contrasteAiguilles(); rendreScene(); rendrePanneau(); majAdresse(); }

/** Lisibilite : aiguilles noires sur cadran clair, argent sur cadran sombre
    (regle `contrasteAiguilles` de regles.json). Appliquee a chaque changement
    de cadran, tant que le client n'a pas choisi ses aiguilles lui-meme :
    des qu'il le fait, son choix est garde. */
function contrasteAiguilles() {
  const r = etat.modele?.contrasteAiguilles;
  if (!r) return;
  const cad = etat.choix[r.cadran], aig = etat.choix[r.aiguilles];
  const cleAig = aig && `${aig.piece}--${aig.variante}`;
  // les aiguilles ont change sans que la regle y soit pour rien : choix client
  if (etat._aiguillesPosees && cleAig !== etat._aiguillesPosees) etat.aiguillesManuelles = true;
  const cleCad = cad && `${cad.piece}--${cad.variante}`;
  if (cleCad !== etat._cadranVu && !etat.aiguillesManuelles && cad) {
    const regle = [r.sombres, r.clairs].find((g) => g?.variantes.includes(cad.variante));
    const [piece, variante] = (regle?.aiguilles || '').split('--');
    if (etat.modele.pieces[piece]?.variantes.some((v) => v.id === variante)) {
      etat.choix[r.aiguilles] = { piece, variante };
      etat.familleActive[r.aiguilles] = piece;
    }
  }
  etat._cadranVu = cleCad;
  const a2 = etat.choix[r.aiguilles];
  etat._aiguillesPosees = a2 && `${a2.piece}--${a2.variante}`;
}

/** Selection d'ouverture : celle que declare le modele (`defaut` dans
    modele.json, par groupe), sinon la premiere variante de chaque calque. */
function choixParDefaut(m) {
  const choix = {};
  for (const c of m.calques || []) {
    const voulu = (m.defaut || {})[c.groupe];
    if (voulu) {
      const [piece, variante] = voulu.split('--');
      if (m.pieces[piece]?.variantes.some((v) => v.id === variante)) {
        choix[c.groupe] = { piece, variante };
        continue;
      }
    }
    const piece = c.pieces.find((p) => m.pieces[p]?.variantes.length);
    if (piece) choix[c.groupe] = { piece, variante: m.pieces[piece].variantes[0].id };
  }
  return choix;
}

/** Le modele courant se lit dans l'adresse : la page est partageable. */
function majAdresse() {
  const u = new URL(location.href);
  u.searchParams.set('modele', etat.modele.id);
  history.replaceState(null, '', u);
}

function chargerModele(id) {
  const m = etat.manifest.modeles.find((x) => x.id === id);
  if (!m) return;
  etat.modele = m;
  etat.choix = choixParDefaut(m);
  etat.aiguillesManuelles = false;
  etat._cadranVu = etat._aiguillesPosees = null;
  etat.familleActive = Object.fromEntries(
    Object.entries(etat.choix).map(([g, c]) => [g, c.piece]));
  $('#nom-modele').textContent = m.nom;
  $('#desc-modele').textContent = m.description || '';
  $('#modeles').value = m.id;
  $('#montre').classList.remove('prete');
  tout();
  prechargerModele(m);
}

async function demarrer() {
  etat.manifest = window.MOURADI_MANIFEST;

  const dispos = etat.manifest.modeles.filter((m) => m._nbVariantes > 0);
  const sel = $('#modeles');
  etat.manifest.modeles.forEach((m) => {
    const o = document.createElement('option');
    o.value = m.id;
    o.textContent = m.nom + (m._nbVariantes ? '' : ' (a venir)');
    o.disabled = m._nbVariantes === 0;
    sel.appendChild(o);
  });
  sel.onchange = () => chargerModele(sel.value);

  if (!dispos.length) {
    $('#controles').innerHTML =
      '<p class="sous-titre">Aucun modele ne contient encore d\'images.</p>';
    return;
  }
  const demande = new URL(location.href).searchParams.get('modele');
  chargerModele(dispos.some((m) => m.id === demande) ? demande : dispos[0].id);
}

demarrer().catch((e) => {
  console.error(e);
  $('#controles').innerHTML =
    `<p class="sous-titre">Chargement impossible : ${e.message}</p>`;
});
